import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarCheck2, DollarSign, Users, FileText, TrendingUp, ClipboardList, School, Mail } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import HousingManager from "@/pages/admin/HousingManager";
import EmailCenter from "@/pages/admin/EmailCenter";

export default function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [appts, setAppts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [reports, setReports] = useState([]);

  function load() {
    Promise.all([
      http.get("/admin/analytics"), http.get("/appointments"),
      http.get("/admin/employees"), http.get("/admin/customers"), http.get("/reports"),
    ]).then(([an, a, e, c, r]) => {
      setAnalytics(an.data); setAppts(a.data);
      setEmployees(e.data); setCustomers(c.data); setReports(r.data);
    });
  }
  useEffect(load, []);

  async function assign(apptId, cleanerId) {
    await http.post(`/appointments/${apptId}/assign`, { cleaner_id: cleanerId });
    toast.success("Cleaner assigned");
    load();
  }

  const chartData = [
    { name: "Mon", v: 6 }, { name: "Tue", v: 9 }, { name: "Wed", v: 7 },
    { name: "Thu", v: 11 }, { name: "Fri", v: 8 }, { name: "Sat", v: 14 }, { name: "Sun", v: 4 },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <Badge variant="outline">Admin</Badge>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-primary mt-2" data-testid="admin-dashboard-title">Operations Dashboard</h1>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat icon={<DollarSign />} label="Monthly revenue" value={`$${analytics?.monthly_revenue?.toFixed(0) ?? 0}`} accent="secondary" />
        <Stat icon={<CalendarCheck2 />} label="Appointments today" value={analytics?.appointments_today ?? 0} />
        <Stat icon={<Users />} label="Active customers" value={analytics?.parents ?? 0} />
        <Stat icon={<School />} label="Universities" value={analytics?.universities ?? 0} accent="secondary" />
      </div>

      <Tabs defaultValue="schedule" className="mt-10">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
          <TabsTrigger value="housing" data-testid="tab-housing">Housing</TabsTrigger>
          <TabsTrigger value="employees" data-testid="tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">Customers</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports-admin">Reports</TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails"><Mail size={12} className="mr-1" /> Emails</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="schedule" className="mt-4">
          {appts.length === 0 ? <Empty /> : (
            <Card><CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Property</th>
                    <th className="text-left p-3">Parent</th>
                    <th className="text-left p-3">Student</th>
                    <th className="text-left p-3">Plan</th>
                    <th className="text-left p-3">Cleaner</th>
                    <th className="text-left p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {appts.map((a) => (
                    <tr key={a.id} className="border-t border-border/60">
                      <td className="p-3">{a.scheduled_date}</td>
                      <td className="p-3 font-medium">{a.property?.name || "—"}</td>
                      <td className="p-3 text-muted-foreground">{a.parent_name}</td>
                      <td className="p-3 text-muted-foreground">{a.student?.name}</td>
                      <td className="p-3"><Badge variant="outline">{a.plan}</Badge></td>
                      <td className="p-3">
                        <Select value={a.assigned_cleaner_id || ""} onValueChange={(v) => assign(a.id, v)}>
                          <SelectTrigger className="w-44 h-8" data-testid={`assign-${a.id}`}><SelectValue placeholder="Assign…" /></SelectTrigger>
                          <SelectContent>
                            {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3"><Badge className="bg-secondary/10 text-secondary">{a.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="employees" className="mt-4">
          <SimpleTable rows={employees} cols={[{ k: "name", l: "Name" }, { k: "email", l: "Email" }, { k: "phone", l: "Phone" }, { k: "created_at", l: "Joined", fmt: (v) => v?.slice(0, 10) }]} />
        </TabsContent>
        <TabsContent value="housing" className="mt-4">
          <HousingManager />
        </TabsContent>
        <TabsContent value="customers" className="mt-4">
          <SimpleTable rows={customers} cols={[{ k: "name", l: "Name" }, { k: "email", l: "Email" }, { k: "phone", l: "Phone" }, { k: "created_at", l: "Joined", fmt: (v) => v?.slice(0, 10) }]} />
        </TabsContent>

        <TabsContent value="reports" className="mt-4">
          {reports.length === 0 ? <Empty /> : (
            <div className="grid md:grid-cols-2 gap-4">
              {reports.map((r) => (
                <Card key={r.id} className="border-border/60"><CardContent className="p-5">
                  <div className="flex justify-between items-start"><div className="font-display font-semibold text-primary">Report</div><Badge>{r.cleanliness_score}/10</Badge></div>
                  <div className="text-xs text-muted-foreground mt-1">{r.created_at?.slice(0, 10)}</div>
                  <p className="text-sm mt-2">{r.comments}</p>
                  {r.maintenance_issues && <p className="text-xs text-destructive mt-2">⚠ {r.maintenance_issues}</p>}
                </CardContent></Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="emails" className="mt-4">
          <EmailCenter />
        </TabsContent>

        <TabsContent value="analytics" className="mt-4">
          <div className="grid lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2 border-border/60"><CardContent className="p-6">
              <div className="font-display font-semibold text-primary mb-4">Cleanings this week</div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="v" fill="hsl(213 73% 25%)" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent></Card>
            <div className="space-y-4">
              <Card className="border-border/60"><CardContent className="p-5">
                <div className="text-xs uppercase text-muted-foreground">Total revenue</div>
                <div className="font-display text-3xl font-bold text-primary mt-1">${analytics?.total_revenue?.toFixed(0) ?? 0}</div>
              </CardContent></Card>
              <Card className="border-border/60"><CardContent className="p-5">
                <div className="text-xs uppercase text-muted-foreground">Active subscriptions</div>
                <div className="font-display text-3xl font-bold text-primary mt-1">{analytics?.active_subscriptions ?? 0}</div>
              </CardContent></Card>
              <Card className="border-border/60"><CardContent className="p-5">
                <div className="text-xs uppercase text-muted-foreground">Outstanding invoices</div>
                <div className="font-display text-3xl font-bold text-primary mt-1">{analytics?.outstanding_invoices ?? 0}</div>
              </CardContent></Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ icon, label, value, accent }) {
  const cls = accent === "secondary" ? "bg-secondary/10 text-secondary" : "bg-primary/5 text-primary";
  return (
    <Card className="border-border/60"><CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cls}`}>{icon}</div>
        <div className="font-display text-2xl font-bold text-primary">{value}</div>
      </div>
      <div className="text-xs text-muted-foreground mt-2 uppercase tracking-wider">{label}</div>
    </CardContent></Card>
  );
}

function SimpleTable({ rows, cols }) {
  if (!rows || rows.length === 0) return <Empty />;
  return (
    <Card><CardContent className="p-0 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>{cols.map((c) => <th key={c.k} className="text-left p-3">{c.l}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border/60">
              {cols.map((c) => <td key={c.k} className="p-3">{c.fmt ? c.fmt(r[c.k]) : (r[c.k] || "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </CardContent></Card>
  );
}

function Empty() { return <Card><CardContent className="p-10 text-center text-muted-foreground">Nothing here yet.</CardContent></Card>; }
